import { authService } from '../../services/auth';
import { candidateInvitesApi } from '../../services/api';
import { ApiError } from '../../services/core.api';
import { t } from '../../services/i18n';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export class RcTenantInvitePage extends HTMLElement {
  private token = '';
  private invite: Awaited<ReturnType<typeof candidateInvitesApi.getPublic>> | null = null;
  private error = '';
  private isLoading = true;
  private isClaiming = false;
  private claimCompleted = false;

  async connectedCallback(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    this.token = params.get('token') || '';

    if (!this.token) {
      this.error = t('tenantInvite.loadError');
      this.isLoading = false;
      this.render();
      return;
    }

    try {
      this.invite = await candidateInvitesApi.getPublic(this.token);
      this.isLoading = false;
      this.render();
      await this.maybeContinueInviteFlow();
    } catch (error) {
      console.error('Failed to load tenant invite:', error);
      this.error = error instanceof ApiError ? error.message : t('tenantInvite.loadError');
      this.isLoading = false;
      this.render();
    }
  }

  private async maybeContinueInviteFlow(): Promise<void> {
    if (!this.invite || !authService.isAuthenticated) {
      return;
    }

    const currentUser = authService.currentUser;
    if (currentUser?.accountMode !== 'tenant') {
      this.render();
      return;
    }

    const currentPath = `${window.location.pathname}${window.location.search}`;
    if (!currentUser.onboardingCompleted) {
      authService.rememberPostOnboardingRedirect(currentPath);
      window.location.href = '/onboarding/';
      return;
    }

    if (!this.invite.alreadyClaimed && !this.claimCompleted && !this.isClaiming) {
      await this.claimInvite();
    }
  }

  private async claimInvite(): Promise<void> {
    this.isClaiming = true;
    this.error = '';
    this.render();

    try {
      await candidateInvitesApi.claim(this.token);
      this.claimCompleted = true;
      if (this.invite) {
        this.invite = { ...this.invite, alreadyClaimed: true };
      }
    } catch (error) {
      console.error('Failed to claim tenant invite:', error);
      this.error = error instanceof ApiError ? error.message : t('tenantInvite.claimError');
    } finally {
      this.isClaiming = false;
      this.render();
    }
  }

  private startLoginFlow(): void {
    const currentPath = `${window.location.pathname}${window.location.search}`;
    authService.rememberRequestedAccountMode('tenant');
    authService.rememberPostLoginRedirect(currentPath);
    authService.rememberPostOnboardingRedirect(currentPath);
    window.location.href = '/login/';
  }

  private render(): void {
    if (this.isLoading) {
      this.innerHTML = `<div class="landing-container"><div class="empty-state card">${t('common.loading')}</div></div>`;
      return;
    }

    if (this.error) {
      this.innerHTML = `<div class="landing-container"><div class="empty-state card">${escapeHtml(this.error)}</div></div>`;
      return;
    }

    if (!this.invite) {
      this.innerHTML = `<div class="landing-container"><div class="empty-state card">${t('tenantInvite.loadError')}</div></div>`;
      return;
    }

    const currentUser = authService.currentUser;
    const isTenantUser = currentUser?.accountMode === 'tenant';
    const showLoginCta = !authService.isAuthenticated;
    const showLandlordModeWarning = authService.isAuthenticated && !isTenantUser;
    const showSuccess = this.invite.alreadyClaimed || this.claimCompleted;

    this.innerHTML = `
      <section class="login-section">
        <div class="landing-container">
          <div class="login-container">
            <div class="login-box">
              <h1>${t('tenantInvite.title')}</h1>
              <p>${t('tenantInvite.subtitle')}</p>
              <div class="candidate-convert-note" style="margin: 16px 0 24px;">
                <span>${t('tenantInvite.freeNote')}</span>
              </div>
              <div class="card" style="padding:16px;text-align:left;margin-bottom:20px;">
                <strong>${escapeHtml(this.invite.propertyAddress)}</strong>
                <p style="margin:8px 0 0;">${escapeHtml(this.invite.landlordDisplayName)} ${t('tenantInvite.invitedBySuffix')}</p>
                <p style="margin:8px 0 0;">${escapeHtml(this.invite.candidateFirstName)} ${escapeHtml(this.invite.candidateLastName)}${this.invite.candidateEmail ? ` - ${escapeHtml(this.invite.candidateEmail)}` : ''}</p>
              </div>
              ${showSuccess ? `<p>${t('tenantInvite.success')}</p><p>${t('tenantInvite.successBody')}</p><a class="btn btn-primary" href="${escapeHtml(this.invite.portalTargetUrl || '/tenant/active/')}">${t('tenantInvite.portalCta')}</a>` : ''}
              ${showLandlordModeWarning ? `<p>${t('publicProperty.applyLandlordModeBlocked')}</p><a class="btn btn-outline" href="/profile/">${t('tenantInvite.profileCta')}</a>` : ''}
              ${showLoginCta ? `<button class="btn btn-primary" data-action="start-login">${t('tenantInvite.loginCta')}</button>` : ''}
              ${authService.isAuthenticated && isTenantUser && !showSuccess && !this.isClaiming ? `<p>${t('tenantInvite.claiming')}</p>` : ''}
              ${this.isClaiming ? `<p>${t('tenantInvite.claiming')}</p>` : ''}
            </div>
          </div>
        </div>
      </section>
    `;

    this.querySelector<HTMLElement>('[data-action="start-login"]')?.addEventListener('click', () => this.startLoginFlow());
  }
}

if (!customElements.get('rc-tenant-invite-page')) {
  customElements.define('rc-tenant-invite-page', RcTenantInvitePage);
}
